/** @format */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { applyTopicToggle } from 'src/utils/contentEditor/applyTopicToggle';
import axios from 'src/utils/axios';
import { ENDPOINTS } from 'src/constants';
import { getContentLanguage } from 'src/utils/locale';
import { resolveExtranetApiLanguageCode } from 'src/utils/extranetLanguages';
import { unwrapContentPostPayload } from 'src/utils/contentEditor/mapContentDetailToEditorPayload';
import type { ContentEditorApiPost, ContentEditorApiResponse, ContentEditorPart, ContentPartType } from 'src/types';

type ContentEditorTag = {
  id: string | number;
  label?: string;
  name?: string;
  isNew?: boolean;
};

function reindexPartWeights(parts: ContentEditorPart[]) {
  if (!Array.isArray(parts)) return;
  parts.forEach((p, i) => {
    p.weight = i;
  });
}

function createEmptyPart(apiType: ContentPartType, weight = 0): ContentEditorPart {
  const id = uuidv4();
  switch (apiType) {
    case 'content':
      return { id, type: 'content', weight, text: '' };
    case 'documents':
      return { id, type: 'documents', weight, documents: [] };
    case 'imageText':
      return { id, type: 'imageText', weight, title: '', text: '', image: null };
    case 'salesInformationNo':
      return { id, type: 'salesInformationNo', weight, salesInformationNo: '' };
    case 'serviceInformationNo':
      return { id, type: 'serviceInformationNo', weight, siNumber: '' };
    case 'video':
      return { id, type: 'video', weight, video: null };
    case 'image':
      return { id, type: 'image', weight, image: null };
    case 'casaNumber':
      return {
        id,
        type: 'casaNumber',
        weight,
        casaLinkActive: false,
        casaPartNumbers: [],
        casaHierarchies: []
      };
    default:
      return { id, type: 'content', weight, text: '' };
  }
}

export interface ContentEditorState {
  mode: string;
  contentId: string | null;
  locale: string;
  title: string;
  parts: ContentEditorPart[];
  topicIds: Array<string | number>;
  selectedTags: ContentEditorTag[];
  sendNotification: boolean;
  releaseDate: string | null;
  editorDetail: ContentEditorApiPost | null;
  editorDetailLoading: boolean;
  editorDetailError: unknown;
  submitError: string | null;
  submitErrorDialogOpen: boolean;
  createContentPostLoading: boolean;
  createContentPostError: unknown;
  updateContentPostLoading: boolean;
  updateContentPostError: unknown;
}

function getInitialState(): ContentEditorState {
  return {
    mode: 'create',
    contentId: null,
    locale: 'en',
    title: '',
    parts: [createEmptyPart('content', 0)],
    topicIds: [],
    selectedTags: [],
    sendNotification: false,
    releaseDate: null,
    editorDetail: null,
    editorDetailLoading: false,
    editorDetailError: null,
    submitError: null,
    submitErrorDialogOpen: false,
    createContentPostLoading: false,
    createContentPostError: null,
    updateContentPostLoading: false,
    updateContentPostError: null
  };
}

const initialState = getInitialState();

export const CONTENT_TOO_LARGE_ERROR_KEY = 'contentEditor.errors.contentTooLarge';

function extractErrorText(error: unknown): string {
  if (error == null) return '';
  if (typeof error === 'string') return error;
  if (typeof error !== 'object') return String(error);

  const record = error as Record<string, unknown>;
  return [record.message, record.error, record.detail, record.title, record.statusText]
    .filter((value): value is string => typeof value === 'string' && Boolean(value.trim()))
    .join(' ');
}

export function getContentPostErrorMessage(error: any, fallback: string): string {
  // Axios interceptor may reject with `{ status }` (stripped response) or full Axios error
  const rawStatus = error?.response?.status ?? error?.status;
  const status = Number(rawStatus);
  const hasStatus = rawStatus != null && rawStatus !== '' && Number.isFinite(status);

  if (hasStatus && status === 413) {
    return CONTENT_TOO_LARGE_ERROR_KEY;
  }

  const text = extractErrorText(error);
  if (/413|content too large|entity too large|request entity too large/i.test(text)) {
    return CONTENT_TOO_LARGE_ERROR_KEY;
  }

  // Large multipart uploads often surface as Network Error / generic message when the
  // gateway returns 413 without CORS headers (DevTools shows 413, JS cannot read status).
  if (
    !hasStatus &&
    (/^network error$/i.test(text.trim()) || /^something went wrong$/i.test(text.trim()))
  ) {
    return CONTENT_TOO_LARGE_ERROR_KEY;
  }

  return text || fallback;
}

/**
 * Helper function to build FormData for creating content posts
 * Converts plain JS object to multipart/form-data structure required by API
 */
export function buildCreateContentPostFormData(contentPost) {
  const formData = new FormData();

  // Add top-level fields
  if (contentPost.title !== undefined) {
    formData.append('contentPost[0][title]', contentPost.title || '');
  }

  if (contentPost.locale !== undefined) {
    formData.append('contentPost[0][locale]', contentPost.locale || '');
  }

  if (contentPost.published !== undefined) {
    formData.append('contentPost[0][published]', String(Boolean(contentPost.published)));
  }

  if (contentPost.releaseDate !== undefined && contentPost.releaseDate) {
    formData.append('contentPost[0][releaseDate]', contentPost.releaseDate);
  }

  if (contentPost.activateNotifications !== undefined) {
    formData.append('contentPost[0][activateNotifications]', String(Boolean(contentPost.activateNotifications)));
  }

  // Add topicIds as array
  if (Array.isArray(contentPost.topicIds)) {
    contentPost.topicIds.forEach(topicId => {
      formData.append('contentPost[0][topicIds][]', String(topicId));
    });
  }

  // Add tagIds as array
  if (Array.isArray(contentPost.tagIds)) {
    contentPost.tagIds.forEach(tagId => {
      formData.append('contentPost[0][tagIds][]', String(tagId));
    });
  }

  if (Array.isArray(contentPost.newTags)) {
    contentPost.newTags.forEach(tag => {
      const value = String(tag ?? '').trim();
      if (value) {
        formData.append('contentPost[0][newTags][]', value);
      }
    });
  }

  // Add content parts
  if (Array.isArray(contentPost.contentParts)) {
    contentPost.contentParts.forEach((part, index) => {
      const partPrefix = `contentPost[0][contentParts][${index}]`;

      // Add type and weight for all parts
      formData.append(`${partPrefix}[type]`, part.type || 'content');
      formData.append(`${partPrefix}[weight]`, String(part.weight || index));

      // Add fields based on part type
      switch (part.type) {
        case 'content':
          formData.append(`${partPrefix}[text]`, part.text || '');
          break;

        case 'casaNumber':
          formData.append(`${partPrefix}[casaLinkActive]`, String(Boolean(part.casaLinkActive)));
          if (Array.isArray(part.casaPartNumbers)) {
            part.casaPartNumbers.forEach(partNumber => {
              formData.append(`${partPrefix}[casaPartNumbers][]`, String(partNumber));
            });
          }
          if (Array.isArray(part.casaHierarchies)) {
            part.casaHierarchies.forEach(hierarchy => {
              formData.append(`${partPrefix}[casaHierarchies][]`, String(hierarchy));
            });
          }
          break;

        case 'salesInformationNo':
          formData.append(`${partPrefix}[salesInformationNo]`, part.salesInformationNo || '');
          break;

        case 'serviceInformationNo':
          formData.append(`${partPrefix}[siNumber]`, part.siNumber || '');
          break;

        case 'imageText':
          formData.append(`${partPrefix}[title]`, part.title || '');
          formData.append(`${partPrefix}[text]`, part.text || '');
          if (part._file && part._file instanceof File) {
            formData.append(`${partPrefix}[image][file]`, part._file);
          } else if (part.image && part.image.id) {
            formData.append(`${partPrefix}[image][id]`, String(part.image.id));
          }
          break;

        case 'image':
          if (part._file && part._file instanceof File) {
            formData.append(`${partPrefix}[image][file]`, part._file);
          } else if (part.image && part.image.id) {
            formData.append(`${partPrefix}[image][id]`, String(part.image.id));
          }
          break;

        case 'video':
          if (part._file && part._file instanceof File) {
            formData.append(`${partPrefix}[video][file]`, part._file);
          } else if (part.video && part.video.id) {
            formData.append(`${partPrefix}[video][id]`, String(part.video.id));
          }
          break;

        case 'documents':
          if (Array.isArray(part.documents)) {
            part.documents.forEach((document, docIndex) => {
              if (document.file && document.file instanceof File) {
                formData.append(`${partPrefix}[documents][${docIndex}][file]`, document.file);
              } else if (document.id) {
                formData.append(`${partPrefix}[documents][${docIndex}][id]`, String(document.id));
              }
            });
          }
          break;
      }
    });
  }

  return formData;
}

/**
 * Helper function to build FormData for updating content posts
 * Converts plain JS object to multipart/form-data structure required by update API
 */
export function buildContentPostFormData(contentPost) {
  const formData = new FormData();

  // Add top-level fields including id for update
  if (contentPost.id !== undefined && contentPost.id !== null) {
    formData.append('contentPost[0][id]', String(contentPost.id));
  }

  if (contentPost.title !== undefined) {
    formData.append('contentPost[0][title]', contentPost.title || '');
  }

  if (contentPost.locale !== undefined) {
    formData.append('contentPost[0][locale]', contentPost.locale || '');
  }

  if (contentPost.published !== undefined) {
    formData.append('contentPost[0][published]', String(Boolean(contentPost.published)));
  }

  if (contentPost.releaseDate !== undefined && contentPost.releaseDate) {
    formData.append('contentPost[0][releaseDate]', contentPost.releaseDate);
  }

  if (contentPost.activateNotifications !== undefined) {
    formData.append('contentPost[0][activateNotifications]', String(Boolean(contentPost.activateNotifications)));
  }

  // Add topicIds as array
  if (Array.isArray(contentPost.topicIds)) {
    contentPost.topicIds.forEach(topicId => {
      formData.append('contentPost[0][topicIds][]', String(topicId));
    });
  }

  // Add tagIds as array
  if (Array.isArray(contentPost.tagIds)) {
    contentPost.tagIds.forEach(tagId => {
      formData.append('contentPost[0][tagIds][]', String(tagId));
    });
  }

  if (Array.isArray(contentPost.newTags)) {
    contentPost.newTags.forEach(tag => {
      const value = String(tag ?? '').trim();
      if (value) {
        formData.append('contentPost[0][newTags][]', value);
      }
    });
  }

  // Add content parts
  if (Array.isArray(contentPost.contentParts)) {
    contentPost.contentParts.forEach((part, index) => {
      const partPrefix = `contentPost[0][contentParts][${index}]`;

      // Add id, type and weight for all parts
      if (part.id !== undefined && part.id !== null) {
        formData.append(`${partPrefix}[id]`, String(part.id));
      }
      formData.append(`${partPrefix}[type]`, part.type || 'content');
      formData.append(`${partPrefix}[weight]`, String(part.weight || index));

      // Add fields based on part type
      switch (part.type) {
        case 'content':
          formData.append(`${partPrefix}[text]`, part.text || '');
          break;

        case 'casaNumber':
          formData.append(`${partPrefix}[casaLinkActive]`, String(Boolean(part.casaLinkActive)));
          if (Array.isArray(part.casaPartNumbers)) {
            part.casaPartNumbers.forEach(partNumber => {
              formData.append(`${partPrefix}[casaPartNumbers][]`, String(partNumber));
            });
          }
          if (Array.isArray(part.casaHierarchies)) {
            part.casaHierarchies.forEach(hierarchy => {
              formData.append(`${partPrefix}[casaHierarchies][]`, String(hierarchy));
            });
          }
          break;

        case 'salesInformationNo':
          formData.append(`${partPrefix}[salesInformationNo]`, part.salesInformationNo || '');
          break;

        case 'serviceInformationNo':
          formData.append(`${partPrefix}[siNumber]`, part.siNumber || '');
          break;

        case 'imageText':
          formData.append(`${partPrefix}[title]`, part.title || '');
          formData.append(`${partPrefix}[text]`, part.text || '');

          // Handle image update/remove/replace
          if (part.image && part.image.id) {
            formData.append(`${partPrefix}[image][id]`, String(part.image.id));
          }
          if (part._imageRemove) {
            formData.append(`${partPrefix}[image][remove]`, 'true');
          } else if (part._file && part._file instanceof File) {
            formData.append(`${partPrefix}[image][file]`, part._file);
          }
          break;

        case 'image':
          // Handle image update/remove/replace
          if (part.image && part.image.id) {
            formData.append(`${partPrefix}[image][id]`, String(part.image.id));
          }
          if (part._imageRemove) {
            formData.append(`${partPrefix}[image][remove]`, 'true');
          } else if (part._file && part._file instanceof File) {
            formData.append(`${partPrefix}[image][file]`, part._file);
          }
          break;

        case 'video':
          // Handle video update/remove/replace
          if (part.video && part.video.id) {
            formData.append(`${partPrefix}[video][id]`, String(part.video.id));
          }
          if (part._videoRemove) {
            formData.append(`${partPrefix}[video][remove]`, 'true');
          } else if (part._file && part._file instanceof File) {
            formData.append(`${partPrefix}[video][file]`, part._file);
          }
          break;

        case 'documents':
          if (Array.isArray(part.documents)) {
            part.documents.forEach((document, docIndex) => {
              if (document.id) {
                formData.append(`${partPrefix}[documents][${docIndex}][id]`, String(document.id));
              }
              if (document._remove) {
                formData.append(`${partPrefix}[documents][${docIndex}][remove]`, 'true');
              } else if (document.file && document.file instanceof File) {
                formData.append(`${partPrefix}[documents][${docIndex}][file]`, document.file);
              }
            });
          }
          break;
      }
    });
  }

  return formData;
}

export const createContentPost = createAsyncThunk(
  'contentEditor/createContentPost',
  async (contentPost: unknown, { rejectWithValue }) => {
    try {
      const formData = buildCreateContentPostFormData(contentPost);
      const response = await axios.post(ENDPOINTS.GET_CONTENT_POST_ENDPOINT, formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getContentPostErrorMessage(error, 'Failed to create content post'));
    }
  }
);

export const updateContentPost = createAsyncThunk(
  'contentEditor/updateContentPost',
  async ({ id, contentPost }: { id: string | number; contentPost: any }, { rejectWithValue }) => {
    try {
      const formData = buildContentPostFormData({ ...contentPost, id });
      const response = await axios.put(`${ENDPOINTS.GET_CONTENT_POST_ENDPOINT}/${id}`, formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(getContentPostErrorMessage(error, 'Failed to update content post'));
    }
  }
);

export const fetchContentPostForEditor = createAsyncThunk(
  'contentEditor/fetchContentPostForEditor',
  async ({ id, language }: { id: string | number; language?: string | null }, { rejectWithValue }) => {
    if (id === undefined || id === null || id === '') {
      return rejectWithValue('Content id is required');
    }

    try {
      const apiLanguage = language ? getContentLanguage(resolveExtranetApiLanguageCode(language) || language) : null;
      const response = await axios.get<ContentEditorApiResponse>(
        `${ENDPOINTS.GET_CONTENT_POST_ENDPOINT}/${id}/editor`,
        {
          ...(apiLanguage ? { acceptLanguage: apiLanguage } : {})
        }
      );
      const detail = unwrapContentPostPayload(response?.data);
      if (!detail) {
        return rejectWithValue('Content detail is empty');
      }
      return detail;
    } catch (error) {
      return rejectWithValue(getContentPostErrorMessage(error, 'Failed to load content for editor'));
    }
  }
);

const slice = createSlice({
  name: 'contentEditor',
  initialState,
  reducers: {
    resetContentEditor: () => getInitialState(),
    setMode(state, action) {
      state.mode = action.payload ?? 'create';
    },
    setContentId(state, action) {
      const id = action.payload;
      state.contentId = id == null || id === '' ? null : String(id);
    },
    setLocale(state, action) {
      state.locale = action.payload ?? 'en';
    },
    setTitle(state, action) {
      state.title = typeof action.payload === 'string' ? action.payload : '';
    },
    addPart(state, action) {
      const type = action.payload?.type || 'content';
      const w = state.parts.length;
      state.parts.push(createEmptyPart(type, w));
      reindexPartWeights(state.parts);
    },
    removePart(state, action) {
      const id = action.payload?.id;
      state.parts = state.parts.filter(p => p.id !== id);
      if (state.parts.length === 0) {
        state.parts.push(createEmptyPart('content', 0));
      }
      reindexPartWeights(state.parts);
    },
    patchPart(state, action) {
      const { id, patch } = action.payload || {};
      const idx = state.parts.findIndex(p => p.id === id);
      if (idx === -1) return;
      state.parts[idx] = { ...state.parts[idx], ...patch };
    },
    movePartUp(state, action) {
      const index = action.payload?.index;
      if (typeof index !== 'number' || index < 1) return;
      const arr = state.parts;
      [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      reindexPartWeights(state.parts);
    },
    movePartDown(state, action) {
      const index = action.payload?.index;
      if (typeof index !== 'number' || index < 0 || index >= state.parts.length - 1) return;
      const arr = state.parts;
      [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      reindexPartWeights(state.parts);
    },
    movePartToIndex(state, action) {
      const fromId = action.payload?.fromId;
      const toId = action.payload?.toId;
      if (!fromId || !toId || fromId === toId) return;
      const fromIndex = state.parts.findIndex(p => p.id === fromId);
      const toIndex = state.parts.findIndex(p => p.id === toId);
      if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
      const [moved] = state.parts.splice(fromIndex, 1);
      state.parts.splice(toIndex, 0, moved);
      reindexPartWeights(state.parts);
    },
    setTopicIds(state, action) {
      const ids = Array.isArray(action.payload) ? action.payload : [];
      state.topicIds = [...new Map(ids.map(id => [String(id), id])).values()];
    },
    toggleTopic(state, action) {
      const { topicId, topicTree } = action.payload || {};
      state.topicIds = applyTopicToggle(state.topicIds, topicTree, topicId);
    },
    addTopicIdFromSearch(state, action) {
      const topicId = action.payload?.topicId ?? action.payload;
      if (topicId === undefined || topicId === null) return;
      const key = String(topicId);
      if (state.topicIds.some(id => String(id) === key)) return;
      state.topicIds.push(topicId);
    },
    setSelectedTags(state, action) {
      state.selectedTags = Array.isArray(action.payload) ? action.payload : [];
    },
    addSelectedTag(state, action) {
      const tag = action.payload as { id?: string | number; label?: string; isNew?: boolean };
      if (!tag || tag.id == null) return;
      if (state.selectedTags.some(t => String((t as { id?: unknown }).id) === String(tag.id))) return;
      const nextTag: ContentEditorTag = { id: tag.id, label: tag.label || String(tag.id) };
      if (tag.isNew) nextTag.isNew = true;
      state.selectedTags.push(nextTag);
    },
    removeSelectedTag(state, action) {
      const id = (action.payload as { id?: string | number })?.id;
      state.selectedTags = state.selectedTags.filter(t => String((t as { id?: unknown }).id) !== String(id));
    },
    setSendNotification(state, action) {
      state.sendNotification = Boolean(action.payload);
    },
    setReleaseDate(state, action) {
      state.releaseDate = action.payload ?? null;
    },
    openSubmitErrorDialog(state, action) {
      state.submitError = typeof action.payload === 'string' ? action.payload : 'Unknown error';
      state.submitErrorDialogOpen = true;
    },
    closeSubmitErrorDialog(state) {
      state.submitErrorDialogOpen = false;
      state.submitError = null;
    },
    hydrateFromContentDetail(state, action) {
      const payload = action.payload;
      if (!payload || typeof payload !== 'object') return;
      const base = getInitialState();
      const { editorDetail, editorDetailLoading, editorDetailError } = state;
      Object.assign(state, {
        ...base,
        ...payload,
        editorDetail,
        editorDetailLoading,
        editorDetailError,
        submitError: null,
        submitErrorDialogOpen: false
      });
      reindexPartWeights(state.parts);
    },
    clearCreateContentPostError(state) {
      state.createContentPostError = null;
    },
    clearUpdateContentPostError(state) {
      state.updateContentPostError = null;
    },
    clearEditorDetailError(state) {
      state.editorDetailError = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(createContentPost.pending, state => {
        state.createContentPostLoading = true;
        state.createContentPostError = null;
      })
      .addCase(createContentPost.fulfilled, state => {
        state.createContentPostLoading = false;
        state.createContentPostError = null;
      })
      .addCase(createContentPost.rejected, (state, action) => {
        const errorMessage =
          (typeof action.payload === 'string' ? action.payload : null) ||
          action.error.message ||
          'Failed to create content post';
        state.createContentPostLoading = false;
        state.createContentPostError = errorMessage;
        state.submitError = errorMessage;
        state.submitErrorDialogOpen = true;
      })
      .addCase(updateContentPost.pending, state => {
        state.updateContentPostLoading = true;
        state.updateContentPostError = null;
      })
      .addCase(updateContentPost.fulfilled, state => {
        state.updateContentPostLoading = false;
        state.updateContentPostError = null;
      })
      .addCase(updateContentPost.rejected, (state, action) => {
        const errorMessage =
          (typeof action.payload === 'string' ? action.payload : null) ||
          action.error.message ||
          'Failed to update content post';
        state.updateContentPostLoading = false;
        state.updateContentPostError = errorMessage;
        state.submitError = errorMessage;
        state.submitErrorDialogOpen = true;
      })
      .addCase(fetchContentPostForEditor.pending, state => {
        state.editorDetailLoading = true;
        state.editorDetailError = null;
      })
      .addCase(fetchContentPostForEditor.fulfilled, (state, action) => {
        state.editorDetailLoading = false;
        state.editorDetailError = null;
        state.editorDetail = action.payload;
      })
      .addCase(fetchContentPostForEditor.rejected, (state, action) => {
        state.editorDetailLoading = false;
        state.editorDetail = null;
        state.editorDetailError =
          (typeof action.payload === 'string' ? action.payload : null) ||
          action.error.message ||
          'Failed to load content for editor';
      });
  }
});

export const { reducer } = slice;
export const {
  resetContentEditor,
  setMode,
  setContentId,
  setLocale,
  setTitle,
  addPart,
  removePart,
  patchPart,
  movePartUp,
  movePartDown,
  movePartToIndex,
  setTopicIds,
  toggleTopic,
  addTopicIdFromSearch,
  setSelectedTags,
  addSelectedTag,
  removeSelectedTag,
  setSendNotification,
  setReleaseDate,
  openSubmitErrorDialog,
  closeSubmitErrorDialog,
  hydrateFromContentDetail,
  clearCreateContentPostError,
  clearUpdateContentPostError,
  clearEditorDetailError
} = slice.actions;

export default slice;
